import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { AlertTriangle, Clock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { AssignWarning } from '@/hooks/use-staffing-board';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';

/**
 * Warning codes that leave the tech uncertified for part of the job. Assigning
 * anyway needs a Manager and a typed reason (mirrors the server rule).
 */
const CERTIFICATION_OVERRIDE_CODES = new Set([
  'no_valid_primary_cert',
  'expired_certs',
  'cert_expires_before_assignment',
  'cert_expires_during_assignment',
]);

export const OVERRIDE_REASON_HINT =
  '"{confirmLabel}" stays disabled until you give a reason. The reason is recorded with the assignment so the certification override can be audited.';

interface ConfirmAssignDialogProps {
  open: boolean;
  warnings?: AssignWarning[];
  message?: string;
  title?: string;
  confirmLabel?: string;
  pendingLabel?: string;
  names?: string[];
  /** False when the current user may not override certification warnings. */
  canOverride?: boolean;
  onConfirm: (overrideReason?: string) => void;
  onCancel: () => void;
  isPending?: boolean;
}

export function ConfirmAssignDialog({
  open,
  warnings = [],
  message,
  title = 'Confirm Assignment',
  confirmLabel = 'Assign Anyway',
  names,
  canOverride = true,
  pendingLabel = 'Assigning...',
  onConfirm,
  onCancel,
  isPending,
}: ConfirmAssignDialogProps) {
  const [overrideReason, setOverrideReason] = useState('');
  const requiresOverrideReason = useMemo(
    () => warnings.some((warning) =>
      warning.code != null && CERTIFICATION_OVERRIDE_CODES.has(warning.code)
    ),
    [warnings],
  );
  const overrideBlocked = requiresOverrideReason && !canOverride;

  useEffect(() => {
    if (!open) setOverrideReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {message ?? 'The following issues were detected. Do you want to proceed?'}
          </DialogDescription>
        </DialogHeader>

        {names && names.length > 0 && (
          <ul className="list-disc pl-5 py-2 text-sm text-gray-700 space-y-1 max-h-60 overflow-auto">
            {names.map((name, index) => (
              <li key={index}>{name}</li>
            ))}
          </ul>
        )}

        {warnings.length > 0 && (
        <div className="space-y-2 py-2">
          {warnings.map((w, i) => (
            <div
              key={i}
              className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${
                w.type === 'availability'
                  ? 'border-yellow-300 bg-yellow-50 text-yellow-800'
                  : 'border-orange-300 bg-orange-50 text-orange-800'
              }`}
            >
              {w.type === 'availability' ? (
                <Clock className="mt-0.5 h-4 w-4 flex-shrink-0" />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              )}
              <span>{w.message}</span>
            </div>
          ))}
        </div>
        )}

        {overrideBlocked && (
          <p className="text-sm font-medium text-red-700">
            Only a Manager or Admin can assign anyway with these certification problems.
          </p>
        )}

        {requiresOverrideReason && !overrideBlocked && (
          <div className="space-y-2">
            <Label htmlFor="override-reason">
              Override reason <span className="text-red-600">(required)</span>
            </Label>
            <Textarea
              id="override-reason"
              value={overrideReason}
              onChange={(event) => setOverrideReason(event.target.value)}
              placeholder="Explain why this certification override is approved"
              required
              aria-required="true"
              aria-describedby="override-reason-hint"
            />
            <p id="override-reason-hint" className="text-xs text-gray-500">
              {OVERRIDE_REASON_HINT.replace('{confirmLabel}', confirmLabel)}
            </p>
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(overrideReason.trim() || undefined)}
            disabled={isPending || overrideBlocked || (requiresOverrideReason && !overrideReason.trim())}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isPending ? pendingLabel : confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
